import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { UserPreferences } from '@/lib/types';
import { useToast } from './use-toast';
import { useState, useEffect } from 'react';

export function useUnitsPreference() {
  const { toast } = useToast();
  const [isMetric, setIsMetric] = useState(false);
  
  // Fetch user preferences from API
  const { data, isLoading, error } = useQuery<UserPreferences>({
    queryKey: ['/api/preferences/'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/preferences/');
      return response.json();
    },
  });

  // Set initial state from API response
  useEffect(() => {
    if (data && data.use_metric_units !== null && data.use_metric_units !== undefined) {
      setIsMetric(!!data.use_metric_units);
    }
  }, [data]);

  // Toggle units preference
  const toggleUnitsMutation = useMutation({
    mutationFn: async (use_metric_units: boolean) => {
      const response = await apiRequest('PUT', '/api/preferences/', {
        use_metric_units,
      });
      return response.json();
    },
    onSuccess: (updatedPrefs) => {
      queryClient.invalidateQueries({ queryKey: ['/api/preferences/'] });
      setIsMetric(!!updatedPrefs.use_metric_units);
      toast({
        title: 'Units Updated',
        description: `Now using ${updatedPrefs.use_metric_units ? 'metric' : 'imperial'} units`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update units preference',
        variant: 'destructive',
      });
    },
  });

  const toggleUnits = () => {
    toggleUnitsMutation.mutate(!isMetric);
  };

  return {
    isMetric,
    toggleUnits,
    isLoading,
    error,
  };
}